import { ResultConfig } from '../../features/test-series/model/test-series.model';
import { formatDisplayDateTime, parseBackendDateArray } from './date.util';

export interface ResultReleaseContext {
  /** When the candidate submitted (or the attempt was auto-completed). */
  submittedAt?: number | null;
  /** When the attempt timer runs out (startedAt + duration). */
  testEndAt?: number | null;
  /** The exam's expiryDateTime; only AFTER_TEST_END waits for it. */
  expiryTimestamp?: number | null;
  timeZone?: string | null;
  /** The API's reviewAvailableAt (date array or ISO string); wins over resultConfig when sent. */
  reviewAvailableAt?: number[] | string | null;
}

/**
 * Timestamp (ms) from which the candidate may review results. The API's
 * reviewAvailableAt is used when sent; otherwise it is worked out from the
 * exam's resultConfig. null means review is available right after submission.
 *
 * - AFTER_SUBMISSION: right after submission.
 * - AFTER_DELAY: submission + delayMinutes.
 * - AT_DATE_TIME: at releaseDateTime.
 * - AFTER_TEST_END: once the exam expires (expiryDateTime). An exam without
 *   an expiryDateTime never expires, so review opens right after submission.
 */
export function getReviewUnlockTimestamp(
  config: ResultConfig | null | undefined,
  context: ResultReleaseContext,
): number | null {
  const { submittedAt, testEndAt, expiryTimestamp, timeZone, reviewAvailableAt } = context;

  const reviewAvailableAtTimestamp = parseReleaseDateTime(reviewAvailableAt, timeZone);
  if (reviewAvailableAtTimestamp) {
    return reviewAvailableAtTimestamp;
  }

  switch (normalizeReleaseType(config)) {
    case 'AFTER_TEST_END':
      return expiryTimestamp || null;
    case 'AT_DATE_TIME':
      return parseReleaseDateTime(config?.releaseDateTime, timeZone);
    case 'AFTER_DELAY': {
      const base = submittedAt ?? testEndAt;
      return base ? base + getDelayMinutes(config) * 60_000 : null;
    }
    case 'AFTER_SUBMISSION':
    default:
      return null;
  }
}

export function isReviewLockedAt(unlockAt: number | null | undefined, now = Date.now()): boolean {
  return !!unlockAt && now < unlockAt;
}

export function getReviewLockedMessage(
  config: ResultConfig | null | undefined,
  unlockAt: number | null | undefined,
): string {
  const unlockAtText = unlockAt ? formatUnlockTime(unlockAt) : '';

  if (normalizeReleaseType(config) === 'AFTER_TEST_END') {
    return unlockAtText
      ? `Once the test expires (${unlockAtText}), you will be able to review your results.`
      : 'Once the test has expired, you will be able to review your results.';
  }

  return unlockAtText
    ? `Results will be available for review on ${unlockAtText}.`
    : 'Results will be available for review soon.';
}

/**
 * "Result will be announced on" text shown before the test starts, as set by
 * the exam's resultConfig.
 */
export function getResultAnnouncementText(
  config: ResultConfig | null | undefined,
  context: ResultReleaseContext,
): string {
  const { expiryTimestamp, timeZone } = context;

  switch (normalizeReleaseType(config)) {
    case 'AFTER_TEST_END':
      return expiryTimestamp ? formatUnlockTime(expiryTimestamp) : 'Immediately after submission';
    case 'AT_DATE_TIME': {
      const releaseAt = parseReleaseDateTime(config?.releaseDateTime, timeZone);
      return releaseAt ? formatUnlockTime(releaseAt) : 'Immediately after submission';
    }
    case 'AFTER_DELAY': {
      const delayMinutes = getDelayMinutes(config);
      return delayMinutes
        ? `${formatDelay(delayMinutes)} after submission`
        : 'Immediately after submission';
    }
    case 'AFTER_SUBMISSION':
    default:
      return 'Immediately after submission';
  }
}

function getDelayMinutes(config: ResultConfig | null | undefined): number {
  return Math.max(Number(config?.delayMinutes) || 0, 0);
}

function formatDelay(totalMinutes: number): string {
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const plural = (value: number, unit: string) => `${value} ${unit}${value === 1 ? '' : 's'}`;

  return [
    days ? plural(days, 'day') : '',
    hours ? plural(hours, 'hour') : '',
    minutes ? plural(minutes, 'minute') : '',
  ].filter(Boolean).join(' ');
}

function normalizeReleaseType(config: ResultConfig | null | undefined): string | undefined {
  return config?.releaseType?.trim().toUpperCase();
}

function parseReleaseDateTime(value: ResultConfig['releaseDateTime'] | undefined, timeZone?: string | null): number | null {
  if (Array.isArray(value)) {
    return parseBackendDateArray(value, timeZone) || null;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
}

function formatUnlockTime(timestamp: number): string {
  return formatDisplayDateTime(timestamp);
}
